import type { ReactNode } from 'react';
import styles from './AiProvenance.module.css';

/**
 * The chip's wording when the artifact is still as the AI produced it.
 *
 * §5 lists four house phrasings — "AI DRAFTED · EDITABLE", "AI SUGGESTED", "AI steps · you
 * approve", "AI draft · you send" — because the chip "names the human's role" and that role differs
 * per surface. This is the default; each AI feature passes its own.
 */
export const AI_LABEL_DEFAULT = 'AI DRAFTED · EDITABLE';

/**
 * The edited-state wording, fixed rather than caller-supplied.
 *
 * The one string in this file that is NOT a parameter: §5 specifies it exactly, and FS-3 AC-18 asserts
 * it literally. A per-surface edited label would let eight features each phrase "a human changed
 * this" differently, which is the drift the shared component exists to prevent.
 */
export const AI_LABEL_EDITED = 'AI DRAFTED · EDITED BY YOU';

export interface AiChipProps {
    /**
     * UPPERCASE, per §9's carve-out: sentence case is the rule everywhere EXCEPT the `type/micro`
     * eyebrow style, which this is. Passed already-cased rather than transformed here, so the
     * string in the source is the string on screen and a test can assert it.
     *
     * `| undefined` explicitly, for the reason `Input`'s test hook carries the same note:
     * `exactOptionalPropertyTypes` is on, and `AiProvenance` forwards its own optional `label`
     * straight through — which is passing undefined, not omitting the key.
     */
    readonly label?: string | undefined;
    /** Flips the label to `AI DRAFTED · EDITED BY YOU`, overriding `label`. */
    readonly edited?: boolean;
    readonly 'data-testid'?: string | undefined;
}

/**
 * §5's `tone/ai` chip — element one of four.
 *
 * **`tone/ai` is reserved for AI provenance and nothing else** (§2 says so on the token itself), so
 * this component is the only thing in the package that reads those two tokens. That is what makes
 * the tone mean something on screen: a user learns "this green is the AI talking" precisely because
 * nothing else uses it.
 *
 * **The tone does NOT change when edited.** Provenance is still AI-originated once a human has
 * touched it — what changes is the wording, because the editable affordance has been taken.
 */
export function AiChip({ label = AI_LABEL_DEFAULT, edited = false, 'data-testid': testId }: AiChipProps) {
    return (
        <span className={styles.chip} data-edited={edited} data-testid={testId}>
            {edited ? AI_LABEL_EDITED : label}
        </span>
    );
}

/** The per-field chip's wording: the block above the fields names the person's role once (§5). */
const AI_FIELD_LABEL = 'AI DRAFTED';

/** The marker's text equivalent, read with the field's label (F12 spec NFR-7). */
const AI_FIELD_TEXT = 'AI drafted';

/** The same, once the person's value differs from the AI's. */
const AI_FIELD_TEXT_EDITED = 'AI drafted, edited by you';

export interface AiFieldMarkerProps {
    /** The person's value differs from the AI's: the chip reads `AI DRAFTED · EDITED BY YOU`. */
    readonly edited?: boolean;
    readonly 'data-testid'?: string | undefined;
}

/**
 * §5's per-field marker — the chip beside the label of each field an AI pre-filled in a form (F12's
 * *New lead from inquiry*), passed to `Field` as its `labelAddon`. The four-element block
 * (`AiProvenance`) still appears once above the fields; this marks which fields it covers.
 *
 * The chip is hidden from assistive technology and a visually hidden phrase stands in for it, so the
 * control's accessible name reads "Company name AI drafted" rather than spelling out the UPPERCASE
 * eyebrow. It renders nothing interactive, because it sits inside a `<label>`.
 */
export function AiFieldMarker({ edited = false, 'data-testid': testId }: AiFieldMarkerProps) {
    return (
        <span className={styles.fieldMarker} data-edited={edited} data-testid={testId}>
            <span aria-hidden="true" className={styles.fieldChip}>
                <AiChip edited={edited} label={AI_FIELD_LABEL} />
            </span>
            <span className={styles.visuallyHidden}>{edited ? AI_FIELD_TEXT_EDITED : AI_FIELD_TEXT}</span>
        </span>
    );
}

export interface AiConfidenceProps {
    /**
     * 0–100, matching the Lead entity's own `confidence` range. Rendered as a whole number: a
     * percentage with decimals reads as more precision than a model score has.
     */
    readonly value: number;
    readonly 'data-testid'?: string | undefined;
}

/**
 * §5's numeric confidence — element two of four.
 *
 * **The NUMBER is the channel, and that is a requirement rather than a preference.** The PRD has
 * F11 score leads P1–P4 with a confidence percentage, so the figure is data the user acts on. §2's
 * colour rule applies here as it does to status: if a tone ever tints this, the number still has to
 * carry the meaning on its own — which is why there is no tone prop to pass.
 *
 * Clamped rather than trusted: a model or a mapping bug that produced 140 would otherwise render
 * "Confidence 140%" on a buyer-facing screen, and a wrong number that looks deliberate is worse
 * than an obviously missing one.
 */
export function AiConfidence({ value, 'data-testid': testId }: AiConfidenceProps) {
    const percent = Math.round(Math.min(100, Math.max(0, value)));
    return (
        <span className={styles.meta} data-testid={testId}>
            Confidence {percent}%
        </span>
    );
}

export interface AiSourceProps {
    /**
     * What the output was derived from — "inquiry email, 14 Aug", "6 activities, 2 quotes". The
     * `from:` prefix is supplied by the component, so every surface reads the same way.
     */
    readonly children: ReactNode;
    /** Where the source record lives, when it is reachable. Renders the reference as a link. */
    readonly href?: string;
    readonly 'data-testid'?: string | undefined;
}

/**
 * Schemes this component is willing to put in an `href`, and the reason there is a list at all.
 *
 * Every caller of `AiSource` is an AI feature (F11–F16, F25, F36), so this `href` is the one in the
 * package most likely to carry a value derived from model output or a synced mail body — which the
 * repo's own conventions classify as untrusted content. React 19 neutralises `javascript:` on its
 * own (measured: it rewrites the attribute to a `throw`), but it passes `data:` and `vbscript:`
 * through untouched, and relying on a framework's sanitiser for a decision this component can make
 * itself is the wrong dependency. Anything not matching falls through to the plain-text branch,
 * which already exists for the unreachable-record case — so failing honestly costs nothing here.
 */
const ALLOWED_SCHEMES = new Set(['http:', 'https:', 'mailto:']);
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

function isSafeHref(href: string): boolean {
    // Leading C0 controls and spaces are discarded by the URL parser before it reads the scheme,
    // so a value beginning with U+0001 followed by `javascript:` is still a `javascript:` URL to
    // the browser. Strip the same range before testing, or the checks below can be walked past.
    //
    // Done as an index scan rather than a regex on purpose: a pattern for this range has to
    // name a control character, which Biome's noControlCharactersInRegex correctly refuses.
    // Neither `trimStart()` nor `\s` is a substitute -- both leave the non-whitespace C0
    // controls in place, which is precisely the bypass.
    let cut = 0;
    while (cut < href.length && href.charCodeAt(cut) <= 0x20) {
        cut += 1;
    }
    const candidate = href.slice(cut);

    // Protocol-relative: `//host` inherits the page scheme and is an absolute off-site URL, not a
    // local path. Browsers also normalise a leading slash-backslash to `//`, so both shapes are
    // rejected. Written as two startsWith checks rather than one regex because the backslash form
    // needs double escaping inside a pattern, and that is exactly where this check would rot.
    if (candidate.startsWith('//') || candidate.startsWith('/\\')) {
        return false;
    }

    const scheme = HAS_SCHEME.exec(candidate);
    // No scheme at all is a relative reference — a path, query or fragment resolved against this
    // app's own origin — which is what every in-app source link actually looks like. Anything
    // carrying a scheme has to be one of the three we are willing to hand to the browser; note a
    // relative-looking `notes:14` parses as a scheme too, and is correctly refused here.
    return scheme === null || ALLOWED_SCHEMES.has(scheme[0].toLowerCase());
}

/**
 * §5's source reference — element three of four.
 *
 * **This is what makes the human-in-the-loop review real rather than ceremonial.** A rep approving
 * an AI draft has to be able to see what the AI read; `architecture.md` requires every output to be
 * tagged with its source for the same reason. Linked where the record is reachable, plain text
 * where it is not — never omitted either way.
 */
export function AiSource({ children, href, 'data-testid': testId }: AiSourceProps) {
    const body = (
        <>
            {/* One node, so a reader announces "from: inquiry email" as a phrase rather than
                pausing between a label and a value that are not related to each other. */}
            from: {children}
        </>
    );

    // An unreachable record and an unusable href render the same way on purpose: the reference is
    // still shown, so the provenance requirement holds either way, and the only thing lost is the
    // ability to click through to something this component could not vouch for.
    if (href === undefined || !isSafeHref(href)) {
        return (
            <span className={styles.meta} data-testid={testId}>
                {body}
            </span>
        );
    }
    // The link is an `inline-flex` box (its 24px target floor, in the stylesheet). One span holds the
    // whole reference, so it is one flex item: a `source` that is an element would otherwise be a
    // flex item of its own, and the space after `from:` would collapse at the end of the text item.
    return (
        <a className={`${styles.meta} ${styles.link}`} data-testid={testId} href={href}>
            <span>{body}</span>
        </a>
    );
}

export interface AiProvenanceProps {
    readonly confidence: number;
    readonly source: ReactNode;
    readonly sourceHref?: string;
    readonly label?: string;
    readonly edited?: boolean;
    readonly 'data-testid'?: string | undefined;
}

/**
 * All four elements together (FS-3 FR-C7) — the form every AI surface should reach for.
 *
 * **They ship together or the requirement is unmet**, which is §5's own wording and the reason this
 * composed component exists beside the three parts. `project-prd.md § Observability and Audit` and
 * `architecture.md § AI provenance…` both require confidence AND source AND the edited flip; the
 * chip alone covers one of the four, and a chip alone is exactly what eight features would each
 * build if the composed form were not the easy thing to use.
 *
 * The parts stay exported for the surfaces that genuinely need them apart — a dense list showing
 * only the chip per row, with confidence in a column of its own.
 */
export function AiProvenance({
    confidence,
    source,
    sourceHref,
    label,
    edited = false,
    'data-testid': testId,
}: AiProvenanceProps) {
    return (
        <span className={styles.group} data-testid={testId}>
            <AiChip
                data-testid={testId === undefined ? undefined : `${testId}-chip`}
                edited={edited}
                label={label}
            />
            <AiConfidence
                data-testid={testId === undefined ? undefined : `${testId}-confidence`}
                value={confidence}
            />
            <AiSource
                data-testid={testId === undefined ? undefined : `${testId}-source`}
                {...(sourceHref === undefined ? {} : { href: sourceHref })}
            >
                {source}
            </AiSource>
        </span>
    );
}
